import { createSlice, createAction, createListenerMiddleware, TypedStartListening, createAsyncThunk } from "@reduxjs/toolkit";
import { ApplicationState, AppDispatch, resetApp } from "./store";
import { generateInitialState, getPersistedOfflineToken, persistOfflineToken } from "utils/common";
import { getAccessToken } from "api/api";
import keycloak, { KEYCLOAK_SCOPE } from "keycloak";

interface Entity {
    authenticated?: boolean;
    tokenValidationError?: boolean;
}

const sliceName = "authentication";

// Middlewares
export const authenticationListener = createListenerMiddleware();
export const startAuthenticationListener = authenticationListener.startListening as TypedStartListening<ApplicationState, AppDispatch>;

// Actions
export const authenticateFailure = createAction(`${sliceName}/AUTHENTICATE_FAILURE`, (err: any) => {
    return {
        payload: err
    }
});

export const setAuthenticated = createAction(`${sliceName}/SET_AUTHENTICATE`, (authenticated: boolean) => {
    return {
        payload: authenticated
    }
});


// Thunks
export const validateRefreshToken = createAsyncThunk<string, void, { state: ApplicationState }>(
    `${sliceName}/VALIDATE_REFRESH_TOKEN`,
    async (_: any, thunkApi) => {
        try {
            const offlineToken = getPersistedOfflineToken();

            if (thunkApi.getState().authentication.entity?.tokenValidationError) {
                throw new Error('Already tried validation.')
            }

            if (!offlineToken) {
                throw new Error('No persisted offline token found');
            }

            const response = await getAccessToken(offlineToken);

            if (response.data.refresh_expires_in === 0) {
                persistOfflineToken(response.data.refresh_token);
            }

            return thunkApi.fulfillWithValue(response.data.access_token)
        } catch (error) {
            return thunkApi.rejectWithValue((error as any)?.response?.statusText);
        }
    }
);

// Slice
export const initialState = generateInitialState<Entity>();

const authenticationSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addMatcher(setAuthenticated.match, (state, { payload }) => {
            state.isInitialized = true;
            state.isSuccess = true;

            if (!state.entity) {
                state.entity = {}
            }
            state.entity.authenticated = payload;
        });

        builder.addMatcher(authenticateFailure.match, (state, { payload }) => {
            state.isInitialized = true;
            state.isLoading = false;
            state.isError = true;
            state.isSuccess = false;
            state.error = payload;
        });

        builder.addMatcher(resetApp.match, () => {
            return initialState;
        });

        builder.addMatcher(validateRefreshToken.pending.match, (state) => {
            state.isLoading = true;
        });

        builder.addMatcher(validateRefreshToken.rejected.match, (state) => {
            state.isLoading = false;

            if (!state.entity) {
                state.entity = {}
            }

            state.entity.tokenValidationError = false;
        });

        builder.addMatcher(validateRefreshToken.fulfilled.match, (state) => {
            state.isLoading = false;

            if (!state.entity) {
                state.entity = {}
            }

            state.entity.tokenValidationError = true;
        });

    },
});

startAuthenticationListener({
    matcher: validateRefreshToken.fulfilled.match,
    effect: () => {
        keycloak.login({ scope: KEYCLOAK_SCOPE })
    }
});

// TODO: add listnener - trackException on validateRefreshToken.reject

// selectors
export const isAuthenticated = (s: ApplicationState) => Boolean(s.authentication.entity?.authenticated);

export default authenticationSlice;
